import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './DelegateModal.css';
import { DPOS_CONTRACT_ADDRESS, TARGET_VALIDATOR } from '../../config/networkConfig';
import { DposInterface } from '../../typechain-types/dpos_contract_interface.sol/DposInterface';
import dposInterface from '../../artifacts/contracts/dpos_contract_interface.sol/DposInterface.json';
const dposInterfaceAbi = dposInterface.abi;

interface DelegateModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableBalance: string; // In TARA as string
  validatorName?: string; // Optional validator name
}

const DelegateModal: React.FC<DelegateModalProps> = ({ 
  isOpen, 
  onClose, 
  availableBalance,
  validatorName = "Icelands 1st validator" 
}) => {
  // Parse the available balance
  const maxAmount = parseFloat(availableBalance);
  
  // Initialize state
  const [amount, setAmount] = useState<number>(1000); // Default 1000 TARA
  const [inputValue, setInputValue] = useState<string>("1000");
  const [isManualInput, setIsManualInput] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Ref for the modal content
  const modalContentRef = useRef<HTMLDivElement>(null);
  
  // Set the minimum amount
  const minAmount = 1000;
  
  // Handle slider change
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setAmount(newValue);
    setInputValue(newValue.toString());
    setIsManualInput(false);
    setError(null);
  };
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsManualInput(true);
    setError(null);
    
    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      // Enforce min and max
      if (parsedValue >= minAmount && parsedValue <= maxAmount) {
        setAmount(parsedValue);
      } else if (parsedValue < minAmount) {
        setAmount(minAmount);
      } else if (parsedValue > maxAmount) {
        setAmount(maxAmount);
      }
    }
  };
  
  // When manual input loses focus, ensure the value is valid
  const handleInputBlur = () => {
    if (isManualInput) {
      const parsedValue = parseFloat(inputValue);
      if (isNaN(parsedValue) || parsedValue < minAmount) {
        setAmount(minAmount);
        setInputValue(minAmount.toString());
      } else if (parsedValue > maxAmount) {
        setAmount(maxAmount);
        setInputValue(maxAmount.toString());
      } else {
        setAmount(parsedValue);
        setInputValue(parsedValue.toString());
      }
      setIsManualInput(false);
    }
  };
  
  // Handle delegate now button click
  const handleDelegateNow = async () => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Check if amount is valid
      if (amount < minAmount) {
        throw new Error(`Minimum delegation amount is ${minAmount} TARA`);
      }
      
      if (amount > maxAmount) {
        throw new Error(`Insufficient balance. Maximum available is ${maxAmount} TARA`);
      }
      
      // Initialize provider and signer
      if (!window.ethereum) {
        throw new Error('MetaMask or another web3 provider is required');
      }
      
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      
      // Initialize DPOS contract
      const dposContract = new ethers.Contract(
        DPOS_CONTRACT_ADDRESS,
        dposInterfaceAbi,
        signer
      ) as unknown as DposInterface;
      
      // Convert amount to wei (1 TARA = 10^18 wei)
      const amountInWei = ethers.parseEther(amount.toString());
      
      // Call delegate method with target validator address and amount
      const tx = await dposContract.delegate(TARGET_VALIDATOR, {
        value: amountInWei
      });
      
      // Wait for transaction to be mined
      console.log('Delegation transaction submitted:', tx.hash);
      
      // Show success message and close modal
      alert(`Successfully delegated ${amount} TARA to ${validatorName}! Transaction hash: ${tx.hash} - This might take a few minutes!`);
      onClose();
      
    } catch (err) {
      console.error('Error delegating TARA:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while delegating');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle clicks outside the modal to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  
  // Format amount with commas
  const formatAmount = (value: number): string => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="delegate-modal-overlay">
      <div className="delegate-modal-container">
        <div className="delegate-modal frost-effect" ref={modalContentRef}>
          <div className="delegate-modal-aurora"></div>
          
          <button className="delegate-modal-close" onClick={onClose}>
            &times;
          </button>
          
          <h2>Delegate TARA</h2>
          <p className="delegate-modal-subtitle">
            Stake your TARA with {validatorName} to earn rewards
          </p>
          
          <div className="delegate-amount-container">
            <div className="delegate-amount-display">
              <span className="amount-value">{formatAmount(amount)}</span>
              <span className="amount-currency">TARA</span>
            </div>
            
            <div className="delegate-slider-container">
              <input 
                type="range" 
                min={minAmount} 
                max={maxAmount > minAmount ? maxAmount : minAmount + 1000} 
                value={amount} 
                onChange={handleSliderChange}
                className="delegate-slider"
                step={100} // Step by 100 TARA
                disabled={isSubmitting}
              />
              <div className="delegate-slider-labels">
                <span>{formatAmount(minAmount)} TARA</span>
                <span>{formatAmount(maxAmount)} TARA</span>
              </div>
            </div>
            
            <div className="delegate-input-container">
              <label htmlFor="manual-amount">Or enter amount manually:</label>
              <div className="delegate-input-wrapper">
                <input 
                  id="manual-amount"
                  type="text" 
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className="delegate-input"
                  disabled={isSubmitting}
                />
                <span className="delegate-input-suffix">TARA</span>
              </div>
            </div>
          </div>
          
          <div className="delegate-info">
            <div className="info-item">
              <span className="info-label">Available:</span>
              <span className="info-value">{formatAmount(maxAmount)} TARA</span>
            </div>
            <div className="info-item">
              <span className="info-label">Min Delegation:</span>
              <span className="info-value">{formatAmount(minAmount)} TARA</span>
            </div>
          </div>
          
          {error && (
            <div className="delegate-error">
              {error}
            </div>
          )}
          
          <div className="delegate-actions">
            <button 
              className="delegate-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              className="delegate-submit"
              onClick={handleDelegateNow}
              disabled={isSubmitting || amount < minAmount || amount > maxAmount}
            >
              {isSubmitting ? (
                <span className="button-spinner"></span>
              ) : 'Delegate Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelegateModal;